import React, { useCallback, useEffect, useState } from "react";
import { Link2, RefreshCw, ShieldCheck, Smartphone, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const labels = {
  connected: "Conectado",
  qr_required: "Esperando vinculación",
  connecting: "Conectando",
  disconnected: "Desconectado",
  unavailable: "Puente no disponible",
  logged_out: "Sesión cerrada",
  inactive: "Transporte directo inactivo",
  starting: "Iniciando",
  error: "Error de conexión",
  relinking: "Renovando vinculación",
};

export default function WhatsAppDirectConnection() {
  const [status, setStatus] = useState(null);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/api/director/whatsapp/direct/status");
      setStatus(data);
      setNotice("");
    } catch (error) {
      setNotice(formatApiError(error));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 4000);
    return () => window.clearInterval(timer);
  }, [load]);

  const state = status?.state || "starting";
  const connected = Boolean(status?.connected);

  const relink = async () => {
    const confirmed = window.confirm(
      "Esto cerrará únicamente la sesión vinculada de KD9 y generará un QR nuevo. Deberás escanearlo desde WhatsApp Business. ¿Continuar?"
    );
    if (!confirmed) return;
    setBusy(true);
    setNotice("");
    try {
      const { data } = await api.post("/api/director/whatsapp/direct/relink");
      setNotice(data?.message || "Sesión reiniciada. Esperando QR nuevo.");
      await load();
    } catch (error) {
      setNotice(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {notice && <div className="kd9-card p-3 text-sm">{notice}</div>}
      <section className="kd9-card p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Link2 size={18} />
              <h2 className="font-cinzel text-lg">Conexión directa de WhatsApp</h2>
            </div>
            <p className="mt-2 max-w-2xl text-sm opacity-65">
              KD9 genera y valida sus códigos internamente. Esta sesión solo transporta mensajes de WhatsApp y recibe respuestas para el Centro WhatsApp.
            </p>
          </div>
          <button className="kd9-btn-outline rounded-xl px-4 py-2 text-sm" onClick={load} disabled={busy}>
            <RefreshCw size={14} className="mr-2 inline" />{busy ? "Actualizando…" : "Actualizar"}
          </button>
        </div>

        <div className="mt-5 flex items-center gap-3 rounded-xl border p-4" style={{ borderColor: "var(--kd9-border)" }}>
          {connected ? <ShieldCheck size={22} color="#1FA774" /> : <Smartphone size={22} />}
          <div>
            <div className="font-semibold">{labels[state] || state}</div>
            <div className="mt-1 text-xs opacity-60">
              {connected
                ? "La sesión de WhatsApp está vinculada y disponible para envíos y recepción."
                : "La sesión debe quedar conectada antes de que KD9 pueda entregar OTP y avisos por este canal."}
            </div>
          </div>
        </div>

        {connected && (
          <div className="mt-4 rounded-xl border p-4" style={{ borderColor: "rgba(212,175,55,.35)" }}>
            <div className="text-sm font-semibold">Recuperación de vinculación</div>
            <p className="mt-1 text-xs opacity-60">
              Si cambiaste de celular, vinculaste un dispositivo nuevo o WhatsApp empezó a mostrar “Esperando mensaje”, renueva la sesión una sola vez. KD9 eliminará únicamente la sesión del bridge y mostrará un QR limpio.
            </p>
            <button
              type="button"
              className="kd9-btn-outline mt-3 rounded-xl px-4 py-2 text-sm"
              onClick={relink}
              disabled={busy}
            >
              <RotateCcw size={14} className="mr-2 inline" />
              {busy ? "Preparando…" : "Renovar sesión y generar QR"}
            </button>
          </div>
        )}

        {status?.qrAvailable && status?.qrDataUrl && !connected && (
          <div className="mt-5 grid gap-5 md:grid-cols-[auto_1fr] md:items-center">
            <div className="rounded-2xl bg-white p-3">
              <img src={status.qrDataUrl} alt="QR para vincular WhatsApp con KD9" className="h-64 w-64 max-w-full" />
            </div>
            <div className="text-sm">
              <div className="font-semibold">Vincular el número</div>
              <p className="mt-2 opacity-70">
                En el teléfono del número que utilizará KD9 abre WhatsApp → Dispositivos vinculados → Vincular dispositivo y escanea este QR.
              </p>
              <p className="mt-3 text-xs opacity-55">
                El QR cambia por seguridad y KD9 lo renueva automáticamente. Si cambias de celular, WhatsApp cierra la sesión o el vínculo vence, esta pantalla vuelve a generar un QR nuevo sin borrar ninguna otra configuración de KD9.
              </p>
              {status?.qrUpdatedAt && (
                <p className="mt-2 text-[11px] opacity-45">
                  QR actualizado: {new Date(status.qrUpdatedAt).toLocaleTimeString("es-CO")}
                </p>
              )}
            </div>
          </div>
        )}

        {!connected && !status?.qrAvailable && (
          <div className="mt-5 rounded-xl border p-4 text-sm" style={{ borderColor: "var(--kd9-border)" }}>
            <div className="flex items-center gap-2 font-semibold">
              <RefreshCw size={15} className={busy ? "animate-spin" : ""} />
              Generando QR de vinculación…
            </div>
            <p className="mt-2 text-xs opacity-60">
              No necesitas solicitarlo manualmente. KD9 recupera la sesión y mostrará aquí un QR vigente en cuanto WhatsApp lo emita.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
